import { FC, useCallback } from 'react';
import { Button, Card, Form, Modal, Space, Spin, Typography } from 'antd';
import { FileTextOutlined, InboxOutlined } from '@ant-design/icons';
import { useDropzone } from 'react-dropzone';
import moment from 'moment-timezone';
import { useParams } from 'react-router-dom';
import { useReportFileUploadMismo } from '../../../../../../hooks/reports';
import { useContextNotifications } from '../../../../../../context/notifications';
import { useContextReports } from '../../../../../../context/reports';
import { EnumReportTypes, ILoanValue } from '../../../../../../types/reports';
import { ROWS } from '../../../constants';

const { Title, Text } = Typography;

type Props = {
  isOpen: boolean;
  onClose: () => void;
}

const MismoUploadModal: FC<Props> = ({ isOpen, onClose }) => {
  const { id: reportId } = useParams();
  const form = Form.useFormInstance();

  const {
    handleLoansUpdate,
    getReportLoanClosingCosts,
    getReportClosingCosts,
    getLoans,
    isBorrower,
    getSettings
  } = useContextReports();

  const { openNotification } = useContextNotifications();
  const fileUploadMismo = useReportFileUploadMismo();

  const isLoading = fileUploadMismo?.loading;

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];

    if (file && file.type === 'text/xml' || file.name.endsWith('.xml')) {
      await processFile(file);
    } else {
      openNotification?.({
        message: 'Please upload a valid XML file (MISMO 3.4 format)',
        type: 'error'
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/xml': ['.xml'],
      'application/xml': ['.xml']
    },
    multiple: false
  });

  const processFile = async (file: File) => {
    try {
      const formData = new FormData();

      formData.append('file', file);
      formData.append('type', isBorrower ? 'borrower': 'loanOfficer');

      const data = await fileUploadMismo.fetch(formData, reportId);
      const parsedLoan = data?.data?.updatedLoan;
      const hasClosingCosts = data?.data?.hasClosingCosts;

      if (!hasClosingCosts) {
        openNotification?.({
          // eslint-disable-next-line max-len
          message: 'No closing costs found. Please ensure you are uploading the DOC VENDOR EXPORT version of the MISMO 3.4',
          type: 'error'
        });
      }

      if (parsedLoan) {
        const newLoan: ILoanValue = {
          ...parsedLoan,
          startAt: parsedLoan.startAt || moment().format('YYYY-MM-DD HH:mm:ss'),
          prepare: parsedLoan.prepare || 3,
          searchBid: parsedLoan.searchBid || 30,
          close: parsedLoan.close || 21,
          currentInterestRate: parsedLoan.currentInterestRate || parsedLoan.interestRate,
          interestRateWithPoint: parsedLoan.interestRateWithPoint || 0,
          totalLoanPoints: parsedLoan.totalLoanPoints || parsedLoan.loanPoints,
          totalCost: parsedLoan.totalCost || parsedLoan.closingCosts,
          otherDeductions: parsedLoan.otherDeductions ?? 0,
        };

        ROWS.forEach((item) => {
          newLoan[item.name] = {
            value: parsedLoan[item.name],
          };
          if (item.subField) {
            newLoan[item.subField.name] = {
              value: parsedLoan[item.subField.name],
            };
          }
        });

        form.setFieldValue('loans', [newLoan]);

        handleLoansUpdate?.({}, true);
        getReportClosingCosts?.();
        getReportLoanClosingCosts?.();
        getSettings?.(EnumReportTypes.purchase);
        getLoans?.()
      }
    } catch {
      openNotification?.({
        message: 'Failed to parse MISMO 3.4 file. Please ensure it\'s a valid MISMO 3.4 XML file.',
        type: 'error'
      });
    }
  };

  return (
    <Modal
      title={
        <div>
          <Title level={3} style={{ marginBottom: "2px" }}>
            Upload Doc Vendor Export (MISMO 3.4 XML)
          </Title>
          <Text type="secondary" style={{ fontSize: "13px" }}>
            Upload your <strong>Doc Vendor Export</strong> file to auto‑fill all loan details and closing costs.
          </Text>
        </div>
      }
      centered
      open={isOpen}
      onCancel={onClose}
      style={{ maxWidth: '80%' }}
      footer={null}
    >
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <Card>
          <div
            {...getRootProps()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #d9d9d9',
              borderRadius: '6px',
              padding: '40px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? '#fafafa' : '#fff',
              transition: 'all 0.3s ease',
              height: '240px'
            }}
          >
            {isLoading? (
              <div style={{ textAlign: 'center' }}>
                <Spin size="large" />
              </div>
            ) : (
              <div>
                <input {...getInputProps()} />
                <InboxOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                <Title level={4} style={{ marginBottom: '8px' }}>
                  {isDragActive ? 'Drop the Doc Vendor Export File Here' : 'Drag & Drop Doc Vendor Export File'}
                </Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                  or click below to select from your computer
                </Text>
                <Button type="primary" icon={<FileTextOutlined />}>
                  Select Doc Vendor Export File
                </Button>
              </div>
            )}
          </div>
        </Card>
      </Space>
    </Modal>
  );
};

export default MismoUploadModal;
